<template>
  <div class="feedback-modal">
    <Modal
      :modal-active="isModalOpen"
      :model="props.model?.modal"
      :title="t('feedbackModal.title')"
      @close="handleClose"
      :key="componentKey"
    >
      <div class="feedback-content">
        <textarea
          v-model="feedbackMessage"
          class="feedback-input"
          :placeholder="t('feedbackModal.input.placeholder')"
          rows="1"
          maxlength="1000"
          :aria-label="t('feedbackModal.input.alt')"
          :alt="t('feedbackModal.input.alt')"
          :title="t('feedbackModal.input.alt')"
        ></textarea>

        <div class="feedback-actions">
          <Button
            @click="handleClose"
            :model="appStore.configuration?.components.feedbackModal.button"
            :is-disabled="false"
            :key="componentKey"
            :alt="t('feedbackModal.closeButton.alt')"
            :text="t('feedbackModal.closeButton.text')"
            class="feedback-close-button"
            :aria-label="t('feedbackModal.closeButton.alt')"
            :title="t('feedbackModal.closeButton.alt')"
          ></Button>

          <Button
            @click="handleSubmitAsync"
            :model="appStore.configuration?.components.feedbackModal.button"
            :is-disabled="isSubmitButtonDisabled"
            :key="componentKey"
            :alt="t('feedbackModal.submitButton.alt')"
            :text="t('feedbackModal.submitButton.text')"
            class="feedback-submit-button"
            :aria-label="t('feedbackModal.submitButton.alt')"
            :title="t('feedbackModal.submitButton.alt')"
          ></Button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import Modal from './Modal.vue'
import { t } from '../composables/Translation'
import { updateComponentKey } from '../composables/ComponentKey'
// import type { FeedbackModal } from '../types/configuration/FeedbackModal.ts'
import { feedbackAsync } from '../composables/Feedback.ts'
import { useAidaSessionStore } from '../stores/aidaSessionStore.ts'
import { FeedbackGrade } from '../types/FeedbackGrade.ts'
import Button from './Button.vue'
import { useAppStore } from '../stores/appStore.ts'
import type { FeedbackModal } from '../types/configuration/FeedbackModal.ts'

const emit = defineEmits<{
  (e: 'close'): void
}>()
const aidaSessionStore = useAidaSessionStore()
const feedbackMessage = ref('')
const isSubmitButtonDisabled = ref(false)
const appStore = useAppStore()

const props = defineProps<{
  model: FeedbackModal | undefined
  isModalOpen: boolean
  grade: FeedbackGrade | null
}>()

onMounted(async () => {
  if (appStore.configuration) {
    componentKey.value += 1
  }
})

function handleClose() {
  console.log('Feedback canceled')
  feedbackMessage.value = ''
  emit('close')
  // appStore.feedbackModalStateChange(FeedbackGrade.None)
}

async function handleSubmitAsync() {
  isSubmitButtonDisabled.value = true

  let feedbackGrade = props.grade as FeedbackGrade
  if (feedbackGrade !== FeedbackGrade.None) {
    const aida = aidaSessionStore.aidaLibrary
    const answer = await feedbackAsync(aida, feedbackMessage.value, feedbackGrade)
    if (answer) {
      console.log('Answer received: ', answer)
    }
  }

  feedbackMessage.value = ''
  isSubmitButtonDisabled.value = false
  emit('close')
}

let componentKey = updateComponentKey(props)
</script>

<style scoped lang="scss">
.feedback-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.feedback-input {
  width: 90%;
  min-height: 32px;
  max-height: 160px; /* ~5 rows */
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  font: inherit;
  font-size: 14px;
  background-color: var(--background-color);
  color: var(--text-color);
  resize: vertical;
  transition:
    border-color 120ms ease,
    box-shadow 120ms ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);
  }
}

.feedback-actions {
  display: flex;
  justify-content: space-between;
  width: 90%;
  margin-top: 8px;
}

.feedback-submit-button {
  font: inherit;
  font-size: 14px;
  background-color: var(--text-color);
  color: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 8px 16px;
  cursor: pointer;
  transition:
    transform 120ms ease,
    background-color 120ms ease;

  &:hover {
    background-color: rgba(59, 67, 82, 0.9);
  }

  &:active {
    transform: scale(0.96);
  }

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
}

.feedback-close-button {
  font: inherit;
  font-size: 14px;
  background: var(--background-color);
  color: var(--text-color);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 8px 16px;
  cursor: pointer;
  transition:
    transform 120ms ease,
    background-color 120ms ease;

  &:hover {
    background-color: rgba(59, 67, 82, 0.06);
  }

  &:active {
    transform: scale(0.96);
  }

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
}
</style>
